import { Injectable } from '@angular/core';
import {
  FictionInterestId,
  NonFictionInterestId,
  Quiz,
  highlightText,
} from 'funnel/src/app/data/quiz';
import type { Book } from 'shared/src/types/funnel-sample-content';
import {
  BookId,
  defaultBookIds,
  fictionInterestBookIdsMap,
  nonFictionInterestBookIdsMap,
  readerGoalBookIdsMap,
} from '../../../../funnel/src/app/data/books';
import {
  FunnelFlowService,
  QuizStepKey,
} from '../../../../funnel/src/app/services/funnel-flow.service';
import { FunnelFlowConfig } from 'funnel/src/app/flow/flow-config';
import { readerFunnelFlow } from '../reader-funnel.flow';

declare const $localize: any;

@Injectable({
  providedIn: 'root',
})
export class ReaderFunnelFlowService extends FunnelFlowService {
  protected override getFlowConfig(): FunnelFlowConfig {
    return readerFunnelFlow;
  }

  protected override getDefaultIntroTitle(): string {
    return (
      $localize`:@@readerIntroTitlePrefix:Build your ` +
      highlightText($localize`:@@readerIntroTitleHighlight:personal audio reading plan`)
    );
  }

  override customizeStepData(key: QuizStepKey): Quiz | undefined {
    if (key === 'amountInfo') {
      return {
        ...this.steps.amountInfo,
        title: $localize`:@@readerAmountInfoTitle:Join 10+ million book listeners`,
        mediaCite: $localize`:@@readerAmountInfoCite:People use audio reading to finish more books, keep up with long texts, and make progress when sitting down to read is not realistic.`,
      };
    }

    if (key === 'audioExperienceInfo') {
      return {
        ...this.steps.audioExperienceInfo,
        title: $localize`:@@readerAudioInfoTitle:Listen to books the easy way`,
      };
    }

    if (key === 'honest') {
      return {
        ...this.steps.honest,
        title: $localize`:@@readerEffortlessTitle:Make reading effortless`,
        subtitle: $localize`:@@readerEffortlessSubtitle:Listen your way through any book, focused and engaged.`,
        rive: {
          fileName: 'boy_headphones',
          animations: ['Timeline 1'],
          ratio: 1.3 / 1,
        },
      };
    }

    if (key === 'format') {
      return {
        ...this.steps.format,
        title: $localize`:@@readerFormatTitle:Is there anything else you would like to hear aloud?`,
        subtitle: $localize`:@@readerChoiceWontLimit:Your choice will not limit your experience`,
      };
    }

    if (key === 'retentionInfo') {
      return {
        ...this.steps.retentionInfo, // TODO: there is nothing we use from here
        title: $localize`:@@readerRetentionInfoTitle:Turn any text into audio`,
        subtitle: $localize`:@@readerRetentionInfoSubtitle:With Peech, you can listen to more than books, including articles, documents, and more.`,
        rive: {
          fileName: 'happy_girl',
          animations: ['Timeline 1'],
          ratio: 1.3 / 1,
        },
      };
    }

    if (key === 'technologyInfo') {
      return {
        ...this.steps.technologyInfo,
        title: $localize`:@@readerVoicesInfoTitle:Experience the most natural AI voices available`,
        subtitle: $localize`:@@readerVoicesInfoSubtitle:Peech brings together some of the best AI voices from top providers like OpenAI, xAI, Microsoft, and ElevenLabs.`,
        rive: {
          fileName: 'be_more_productive',
          animations: ['Timeline 1'],
          ratio: 1.3 / 1,
        },
      };
    }

    if (key === 'listeningTime') {
      return {
        ...this.steps.listeningTime,
        title: $localize`:@@readerListeningTimeTitle:Set your daily listening goal`,
      };
    }

    if (key === 'book1' || key === 'book2' || key === 'book3' || key === 'yourBook') {
      const data = super.customizeStepData(key) || this.steps[key];

      return {
        ...data,
        title: $localize`:@@readerBookListenTitle:Would you listen to this book in Peech?`,
      };
    }

    if (key === 'prediction') {
      const data = super.customizeStepData(key) || this.steps.prediction;

      return {
        ...data,
        title: $localize`:@@readerPredictionTitle:Ready to make reading easier?`,
        subtitle: $localize`:@@readerPredictionSubtitle:Your audio reading plan is ready, and we reserved a welcome discount for today.`,
      };
    }

    return super.customizeStepData(key);
  }

  protected override getRelevantBooks(): Book[] {
    const relevantBookIds: BookId[] = [];
    const fictionBookIds: BookId[] = [];
    const nonFictionBookIds: BookId[] = [];
    const readerGoalBookIds: BookId[] = [];
    const otherFictionBookIds: BookId[] = [];

    const addBookId = (target: BookId[], bookId: BookId | undefined) => {
      if (bookId && !target.includes(bookId)) {
        target.push(bookId);
      }
    };
    const addBookIds = (target: BookId[], bookIds: readonly BookId[]) => {
      bookIds.forEach(bookId => addBookId(target, bookId));
    };

    this.getFictionInterestIds().forEach(interestId => {
      if (interestId === 'other') {
        addBookIds(otherFictionBookIds, fictionInterestBookIdsMap[interestId]);
      }
    });

    addBookIds(
      fictionBookIds,
      this.getDiverseBookIdsForInterests(
        this.getFictionInterestIds().filter(interestId => interestId !== 'other'),
        fictionInterestBookIdsMap
      )
    );

    this.getNonFictionInterestIds().forEach(interestId => {
      addBookIds(nonFictionBookIds, nonFictionInterestBookIdsMap[interestId]);
    });

    this.getReaderGoalIds().forEach(readerGoalId => {
      addBookIds(readerGoalBookIds, readerGoalBookIdsMap[readerGoalId]);
    });

    const addRelevantBookId = (bookId: BookId | undefined) => addBookId(relevantBookIds, bookId);
    const addRelevantBookIds = (bookIds: readonly BookId[]) => bookIds.forEach(addRelevantBookId);

    if (fictionBookIds.length > 0) {
      addRelevantBookId(fictionBookIds[0]);
      addRelevantBookId(fictionBookIds[1]);
      addRelevantBookId(nonFictionBookIds[0]);
      addRelevantBookIds(readerGoalBookIds);
      addRelevantBookIds(fictionBookIds.slice(2));
      addRelevantBookIds(nonFictionBookIds.slice(1));
    } else {
      addRelevantBookIds(nonFictionBookIds);
      addRelevantBookIds(readerGoalBookIds);
      addRelevantBookIds(otherFictionBookIds);
    }

    addRelevantBookIds(defaultBookIds);

    return this.sampleContentService.getBooks(relevantBookIds);
  }

  protected override findDiverseBook(candidates: Book[], selectedBooks: Book[]): Book | undefined {
    return candidates.find(candidate => !selectedBooks.includes(candidate));
  }

  private getDiverseBookIdsForInterests(
    interestIds: readonly (FictionInterestId | NonFictionInterestId)[],
    map: Partial<Record<FictionInterestId | NonFictionInterestId, readonly BookId[]>>
  ): BookId[] {
    const result: BookId[] = [];
    const groups = interestIds.map(interestId => map[interestId] || []);
    const maxGroupLength = Math.max(0, ...groups.map(group => group.length));

    const addBookId = (bookId: BookId | undefined) => {
      if (bookId && !result.includes(bookId)) {
        result.push(bookId);
      }
    };

    for (let index = 0; index < maxGroupLength; index++) {
      groups.forEach(group => addBookId(group[index]));
    }

    return result;
  }
}
